// src/pages/Transport.js
import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import Header from '../../layouts/Header';
import SideBar from '../../layouts/SideBar';
import { MdEdit, MdDelete, MdVisibility } from 'react-icons/md';
import { HiDotsVertical } from 'react-icons/hi';
import { BASE_URL } from '../../api';

const API_BASE_URL = `${BASE_URL}/api/master`;

const Transport = () => {
  const [transports, setTransports] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [vehicleOwnerships, setVehicleOwnerships] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  // ==================== FETCH DATA ====================
  const fetchTransports = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/transport`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch transports');
      const { data } = await res.json();
      setTransports(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleTypes = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/vehicletype`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch vehicle types');
      const { data } = await res.json();
      setVehicleTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      // Fallback to static
      setVehicleTypes([
        { _id: 1, name: 'LMV' },
        { _id: 2, name: 'HMV' },
      ]);
    }
  };

  const fetchVehicleOwnerships = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/vehicleownership`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch ownerships');
      const { data } = await res.json();
      setVehicleOwnerships(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      // Fallback to static
      setVehicleOwnerships([
        { _id: 1, name: 'Owned' },
        { _id: 2, name: 'Leased' },
      ]);
    }
  };

  useEffect(() => {
    const load = async () => {
      await Promise.all([fetchVehicleTypes(), fetchVehicleOwnerships(), fetchTransports()]);
    };
    if (localStorage.getItem('token')) load();
  }, []);

  // ==================== VALIDATION ====================
  const validate = (data) => {
    const errors = {};
    if (!data.vehicleno?.trim()) errors.vehicleno = 'Vehicle No is required';
    if (!data.ownername?.trim()) errors.ownername = 'Owner Name is required';
    if (!data.vehicletypeid) errors.vehicletypeid = 'Vehicle Type is required';
    if (!data.vehicleownershipid) errors.vehicleownershipid = 'Ownership is required';
    if (!data.mobileno?.trim()) errors.mobileno = 'Mobile is required';
    else if (!/^\d{10}$/.test(data.mobileno)) errors.mobileno = 'Must be 10 digits';
    return errors;
  };

  // ==================== CRUD ====================
  const createTransport = async (data) => {
    const res = await fetch(`${API_BASE_URL}/transport`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to create');
    }
    return res.json();
  };

  const updateTransport = async (id, data) => {
    const res = await fetch(`${API_BASE_URL}/transport/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to update');
    }
    return res.json();
  };

  const deleteTransport = async (id) => {
    const res = await fetch(`${API_BASE_URL}/transport/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to delete');
    }
    return res.json();
  };

  // ==================== MODAL (Add/Edit) ====================
  // ==================== MODAL (Add/Edit) ====================
  const openModal = async (transport = null) => {
    const isEdit = !!transport;

    const { value: form } = await Swal.fire({
      title: isEdit ? 'Edit Transport' : 'Add New Transport',
      html: `
        <div class="space-y-6 text-left p-2">
          
          <div class="bg-gray-50 p-4 rounded-lg border border-gray-100">
            <h3 class="text-xs font-bold text-blue-600 uppercase tracking-wider mb-4 flex items-center gap-2">
              <span class="w-1.5 h-4 bg-blue-600 rounded-full"></span> Vehicle & Owner Details
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-1">Vehicle No <span class="text-red-500">*</span></label>
                <input id="vehicleno" class="swal2-input !m-0 !w-full !text-sm" value="${transport?.vehicleno || ''}" maxlength="15" placeholder="TN 37 AB 1234">
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-1">Owner Name <span class="text-red-500">*</span></label>
                <input id="ownername" class="swal2-input !m-0 !w-full !text-sm" value="${transport?.ownername || ''}" placeholder="Full Name">
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-1">Vehicle Type <span class="text-red-500">*</span></label>
                <select id="vehicletypeid" class="swal2-select !m-0 !w-full !text-sm">
                  <option value="">Select Type</option>
                  ${vehicleTypes.map(t => `<option value="${t._id}" ${t._id === transport?.vehicletypeid?._id ? 'selected' : ''}>${t.name}</option>`).join('')}
                </select>
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-1">Ownership <span class="text-red-500">*</span></label>
                <select id="vehicleownershipid" class="swal2-select !m-0 !w-full !text-sm">
                  <option value="">Select Ownership</option>
                  ${vehicleOwnerships.map(o => `<option value="${o._id}" ${o._id === transport?.vehicleownershipid?._id ? 'selected' : ''}>${o.name}</option>`).join('')}
                </select>
              </div>
            </div>
          </div>

          <div class="bg-gray-50 p-4 rounded-lg border border-gray-100">
            <h3 class="text-xs font-bold text-blue-600 uppercase tracking-wider mb-4 flex items-center gap-2">
              <span class="w-1.5 h-4 bg-blue-600 rounded-full"></span> Technical Specs & Contact
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-1">Length (ft)</label>
                <input id="vehiclelength" type="number" step="0.1" class="swal2-input !m-0 !w-full !text-sm" value="${transport?.vehiclelength || ''}" placeholder="0.0">
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-1">Capacity (tons)</label>
                <input id="vehiclecapacity" type="number" step="0.1" class="swal2-input !m-0 !w-full !text-sm" value="${transport?.vehiclecapacity || ''}" placeholder="0.0">
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-1">Mobile No <span class="text-red-500">*</span></label>
                <input id="mobileno" class="swal2-input !m-0 !w-full !text-sm" value="${transport?.mobileno || ''}" maxlength="10" placeholder="10 Digit Number">
              </div>
            </div>
          </div>

          <div class="bg-gray-50 p-4 rounded-lg border border-gray-100">
            <h3 class="text-xs font-bold text-blue-600 uppercase tracking-wider mb-4 flex items-center gap-2">
              <span class="w-1.5 h-4 bg-blue-600 rounded-full"></span> System Settings
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-1">Current Status</label>
                <select id="status" class="swal2-select !m-0 !w-full !text-sm">
                  <option value="1" ${transport?.status !== 0 ? 'selected' : ''}>Enabled (Active)</option>
                  <option value="0" ${transport?.status === 0 ? 'selected' : ''}>Disabled (Inactive)</option>
                </select>
              </div>
              <div class="flex flex-col justify-end pb-2">
                 <p class="text-[11px] text-gray-500 italic">* Ensure vehicle documents are verified before enabling.</p>
              </div>
            </div>
          </div>

        </div>
      `,
      width: '850px',
      showCancelButton: true,
      confirmButtonText: isEdit ? 'Update Vehicle' : 'Save Vehicle',
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#6b7280',
      customClass: {
        popup: 'rounded-xl shadow-2xl border-none',
        title: 'text-xl font-bold text-gray-800 pt-6',
        confirmButton: 'px-6 py-2.5 rounded-lg font-semibold',
        cancelButton: 'px-6 py-2.5 rounded-lg font-semibold'
      },
      preConfirm: () => {
        const data = {
          ...(isEdit && { _id: transport._id }),
          vehicleno: document.getElementById('vehicleno').value.trim(),
          ownername: document.getElementById('ownername').value.trim(),
          vehicletypeid: document.getElementById('vehicletypeid').value,
          vehicleownershipid: document.getElementById('vehicleownershipid').value,
          vehiclelength: document.getElementById('vehiclelength').value.trim() || '0',
          vehiclecapacity: document.getElementById('vehiclecapacity').value.trim() || '0',
          mobileno: document.getElementById('mobileno').value.trim(),
          status: parseInt(document.getElementById('status').value),
        };
        const errors = validate(data);
        if (Object.keys(errors).length > 0) {
          Swal.showValidationMessage(Object.values(errors).join('<br>'));
          return false;
        }
        return data;
      },
    });

    if (form) {
      try {
        setLoading(true);
        if (isEdit) {
          await updateTransport(transport._id, form);
          toast.success('Vehicle record updated.');
        } else {
          await createTransport(form);
          toast.success('New vehicle added.');
        }
        fetchTransports();
      } catch (err) {
        toast.error(err.message || 'Error');
      } finally {
        setLoading(false);
      }
    }
  };

  // ==================== DELETE ====================
  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: 'Delete Transport?',
      text: 'This cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#d33',
    });
    if (confirm.isConfirmed) {
      try {
        setLoading(true);
        await deleteTransport(id);
        toast.success('Transport removed.');
        fetchTransports();
      } catch (err) {
        toast.error(err.message || 'Error');
      } finally {
        setLoading(false);
      }
    }
  };

  // ==================== VIEW DETAILS ====================
  const handleView = (t) => {
    const type = vehicleTypes.find(v => v._id === t.vehicletypeid?._id);
    const own = vehicleOwnerships.find(o => o._id === t.vehicleownershipid?._id);
    Swal.fire({
      title: `Vehicle: ${t.vehicleno}`,
      html: `
        <div class="text-left text-sm space-y-2">
          <p><strong>Owner:</strong> ${t.ownername}</p>
          <p><strong>Type:</strong> ${type?.name || 'N/A'}</p>
          <p><strong>Ownership:</strong> ${own?.name || 'N/A'}</p>
          <p><strong>Length:</strong> ${t.vehiclelength} ft</p>
          <p><strong>Capacity:</strong> ${t.vehiclecapacity} tons</p>
          <p><strong>Mobile:</strong> ${t.mobileno}</p>
          <p><strong>Status:</strong> <span class="px-2 py-1 rounded text-xs ${t.status === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}">
            ${t.status === 1 ? 'Enabled' : 'Disabled'}
          </span></p>
        </div>
      `,
      width: '500px',
    });
  };

  // ==================== FILTER & PAGINATION ====================
  const filtered = transports.filter(t =>
    t.vehicleno?.toLowerCase().includes(filter.toLowerCase()) ||
    t.ownername?.toLowerCase().includes(filter.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <>
      <div className="flex">
      <ToastContainer />
        <div className="flex-1 p-6 bg-gray-100 min-h-screen">
          <h2 className="text-2xl font-bold mb-6">Transport Master</h2>

          <div className="bg-white rounded-xl shadow p-6">
            {/* Toolbar */}
            <div className="flex flex-wrap gap-4 mb-6 justify-between">
              <input
                type="text"
                placeholder="Search by Vehicle No or Owner..."
                value={filter}
                onChange={e => setFilter(e.target.value)}
                className="px-4 py-2 border rounded-md w-64"
              />
              <button
                onClick={() => openModal()}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add Transport
              </button>
            </div>

            {/* Table */}
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full table-auto border-collapse">
                    <thead className="bg-blue-600 text-white">
                      <tr>
                        <th className="p-3 text-left">S.No</th>
                        <th className="p-3 text-left">Vehicle No</th>
                        <th className="p-3 text-left">Owner</th>

                        <th className="p-3 text-left">Length</th>
                        <th className="p-3 text-left">Capacity</th>
                        <th className="p-3 text-left">Mobile</th>
                        <th className="p-3 text-left">Status</th>
                        <th className="p-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.length === 0 ? (
                        <tr><td colSpan="9" className="text-center py-8 text-gray-500">No transports found</td></tr>
                      ) : (
                        paginated.map((t, i) => {
                          const type = vehicleTypes.find(v => v._id === t.vehicletypeid?._id);
                          return (
                            <tr key={t._id} className="border-b hover:bg-gray-50">
                              <td className="p-3">
                                {filter
                                  ? transports.findIndex(orig => orig._id === t._id) + 1
                                  : (currentPage - 1) * pageSize + i + 1}
                              </td>
                              <td className="p-3 font-medium">{t.vehicleno}</td>
                              <td className="p-3">{t.ownername}</td>

                              <td className="p-3">{t.vehiclelength}</td>
                              <td className="p-3">{t.vehiclecapacity}</td>
                              <td className="p-3">{t.mobileno}</td>
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded text-xs ${t.status === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                  {t.status === 1 ? 'Enabled' : 'Disabled'}
                                </span>
                              </td>
                              <td className="p-3 text-center">
                                <div className="flex justify-center gap-2">
                                  <button onClick={() => openModal(t)} className="text-blue-600 hover:text-blue-800">
                                    <MdEdit className="w-5 h-5" />
                                  </button>
                                  <button onClick={() => handleDelete(t._id)} className="text-red-600 hover:text-red-800">
                                    <MdDelete className="w-5 h-5" />
                                  </button>
                                  <button onClick={() => handleView(t)} className="text-green-600 hover:text-green-800">
                                    <MdVisibility className="w-5 h-5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-between items-center mt-6">
                    <div>
                      Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, filtered.length)} of {filtered.length}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1">Page {currentPage} of {totalPages}</span>
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Transport;